import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import useStore from "../../utils/store";
import { IoClose } from "react-icons/io5";
import { FiSearch, FiChevronRight } from "react-icons/fi";

const categorizeLinks = (links) => {
  const sections = {
    Overview: [],
    "Sales & Billing": [],
    Logistics: [],
    "Parties & Clients": [],
    "Products & Pricing": [],
    "Team & Staff": [],
    System: [],
  };

  links?.forEach((link) => {
    const t = link.title.toLowerCase().trim();
    if (t.includes("dashboard")) {
      sections["Overview"].push(link);
    } else if (
      t.includes("invoice") ||
      t.includes("order") ||
      t.includes("payment") ||
      t.includes("purchase")
    ) {
      sections["Sales & Billing"].push(link);
    } else if (
      t.includes("dispatch") ||
      t.includes("challan")
    ) {
      sections["Logistics"].push(link);
    } else if (
      t.includes("customer") ||
      t.includes("trader") ||
      t.includes("client") ||
      t.includes("city")
    ) {
      sections["Parties & Clients"].push(link);
    } else if (
      t.includes("product") ||
      t.includes("price") ||
      t.includes("gst")
    ) {
      sections["Products & Pricing"].push(link);
    } else if (
      t.includes("staff") ||
      t.includes("incentive") ||
      t.includes("salary")
    ) {
      sections["Team & Staff"].push(link);
    } else {
      sections["System"].push(link);
    }
  });

  return Object.entries(sections).filter(([_, items]) => items.length > 0);
};

const Sidebar = ({ links, open, setOpen }) => {
  const location = useLocation();
  const ref = useRef(null);
  const [filterText, setFilterText] = useState("");

  const startDate = useStore((state) => state.startDate);
  const user = useStore((state) => state.user);
  const endDate = useStore((state) => state.endDate);
  const setStartDate = useStore((state) => state.setStartDate);
  const setEndDate = useStore((state) => state.setEndDate);

  // Close sidebar on route change
  useEffect(() => {
    if (setOpen) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Click outside listener
  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        if (setOpen) setOpen(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Grouped sections
  const sections = useMemo(() => {
    if (!links || links.length === 0) return [];
    if (!filterText || filterText.trim() === "") {
      return categorizeLinks(links);
    }
    const q = filterText.toLowerCase().trim();
    const filtered = links.filter((l) => l.title.toLowerCase().includes(q));
    return [["Search Results", filtered]];
  }, [links, filterText]);

  const userName = user?.admin?.userName || user?.userName || "User";
  const userRole = user?.type || "Admin";

  return (
    <>
      {/* Dimmed Backdrop with Blur */}
      <div
        className={`fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Slide-out Drawer Panel */}
      <aside
        ref={ref}
        className={`fixed top-0 left-0 bottom-0 w-[280px] sm:w-[295px] bg-[#0f172a] text-slate-200 z-50 shadow-2xl flex flex-col border-r border-slate-800/80 transition-transform duration-300 ease-in-out font-sans ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 bg-[#0b1120]">
          <div className="flex items-center gap-3">
            <div className="bg-white p-1.5 rounded-lg shadow-sm border border-slate-700/50 flex items-center justify-center">
              <img src={logo} alt="Prabhu Labels" className="h-7 w-auto object-contain" />
            </div>
            <div>
              <h2 className="font-bold text-white text-sm tracking-tight leading-none">
                Prabhu Labels
              </h2>
              <span className="inline-block mt-1 text-[10px] font-bold text-emerald-400 tracking-wider uppercase bg-emerald-950/60 border border-emerald-800/50 px-1.5 py-0.2 rounded">
                {userRole} Portal
              </span>
            </div>
          </div>

          <button
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-white hover:bg-slate-800 p-1.5 rounded-lg transition active:scale-95 cursor-pointer"
            title="Close Menu"
          >
            <IoClose className="text-xl" />
          </button>
        </div>

        {/* Search / Filter Input */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-800/50 bg-[#0f172a]">
          <div className="relative flex items-center">
            <FiSearch className="absolute left-3 text-slate-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder="Search menu..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-900/90 text-slate-200 placeholder-slate-500 border border-slate-800 rounded-lg focus:outline-none focus:border-emerald-500/70 focus:ring-1 focus:ring-emerald-500/30 transition-all font-medium"
            />
            {filterText && (
              <button
                onClick={() => setFilterText("")}
                className="absolute right-2.5 text-slate-400 hover:text-slate-200 text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Mobile Date Range Filter */}
        {userRole === "Admin" && (
          <div className="md:hidden px-4 py-2.5 border-b border-slate-800 bg-slate-900/60">
            <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase block mb-1">
              Date Filter
            </span>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  window.location.reload();
                }}
                className="bg-slate-950 border border-slate-700 text-slate-200 text-[11px] py-1 px-1.5 rounded w-full outline-none focus:border-emerald-500"
                max={endDate}
              />
              <span className="text-slate-500 text-xs">to</span>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  window.location.reload();
                }}
                className="bg-slate-950 border border-slate-700 text-slate-200 text-[11px] py-1 px-1.5 rounded w-full outline-none focus:border-emerald-500"
              />
            </div>
          </div>
        )}

        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4 custom-scrollbar">
          {sections.map(([sectionTitle, sectionItems]) => (
            <div key={sectionTitle} className="space-y-1">
              {/* Section Header */}
              <div className="px-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider select-none flex items-center justify-between">
                <span>{sectionTitle}</span>
              </div>

              {/* Section Links */}
              <div className="space-y-0.5">
                {sectionItems.map((item, idx) => {
                  const isActive = location.pathname === item.path;
                  const isLogout = item.path === "/logout";

                  return (
                    <Link
                      to={item.path}
                      key={idx}
                      className={`group flex items-center justify-between px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                        isLogout
                          ? "text-rose-400 hover:bg-rose-950/30 hover:text-rose-300 mt-2 border border-rose-900/30 font-semibold"
                          : isActive
                          ? "bg-emerald-500/15 text-emerald-300 font-semibold border-l-2 border-emerald-400 pl-2.5 shadow-xs"
                          : "text-slate-300 hover:text-white hover:bg-slate-800/60 hover:pl-3.5"
                      }`}
                    >
                      <span className="truncate">{item.title?.trim()}</span>

                      {isActive ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/80" />
                      ) : (
                        <FiChevronRight className="text-slate-600 opacity-0 group-hover:opacity-100 text-xs transition-opacity" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Professional User Profile Footer */}
        <div className="p-3 border-t border-slate-800/80 bg-[#0b1120] flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold flex items-center justify-center text-xs shrink-0">
              {userName.charAt(0).toUpperCase()}
            </div>
            <div className="truncate">
              <p className="text-xs font-semibold text-white truncate leading-tight">
                {userName}
              </p>
              <p className="text-[10px] text-slate-400 truncate leading-tight mt-0.5">
                {userRole}
              </p>
            </div>
          </div>

          <Link
            to="/logout"
            className="text-slate-400 hover:text-rose-400 p-1.5 rounded-md hover:bg-slate-800/60 transition text-xs font-semibold"
            title="Sign Out"
          >
            Sign Out
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
